import {
  Button,
  Card,
  Flex,
  Group,
  Image,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconArrowDown,
  IconArrowUp,
  IconPhoto,
  IconPlus,
  IconVideo,
} from "@tabler/icons-react";
import type React from "react";
import { useEffect, useState } from "react";
import { client } from "@/api-client";
import type { FileOutput } from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { useFileUploadModal } from "@/components/FileUploadModal";
import { Header } from "@/components/Header";
import { UniversalRoutes } from "@/routers/routes";

export const Gallery: React.FC = () => {
  const [galleryFolderId, setGalleryFolderId] = useState<string | null>(null);

  // Get current gallery
  const galleryQuery = client.useQuery("get", "/trainer/profile/gallery");

  // Get all files to find the Gallery folder
  const filesQuery = client.useQuery("get", "/universal/files/");

  // Update gallery mutation
  const updateGalleryMutation = client.useMutation(
    "post",
    "/trainer/profile/gallery",
  );

  // File upload modal hook
  const { open: openFileUpload, modal: fileUploadModal } = useFileUploadModal({
    onSuccess: (_file: FileOutput) => {
      filesQuery.refetch();
    },
    accept: "image/*,video/*",
    folderId: galleryFolderId ?? undefined,
  });

  // Find Gallery folder
  useEffect(() => {
    if (filesQuery.data) {
      const galleryFolder = filesQuery.data.folders.find(
        (folder) => folder.specialType === "GALLERY",
      );
      if (galleryFolder) {
        setGalleryFolderId(galleryFolder.id);
      }
    }
  }, [filesQuery.data]);

  // Get files from Gallery folder
  const galleryFiles =
    filesQuery.data?.files.filter(
      (file) =>
        file.parentFolderId === galleryFolderId &&
        (file.type === "IMAGE" || file.type === "VIDEO") &&
        file.isPublic,
    ) || [];

  // Get current gallery file IDs for filtering
  const currentGalleryFileIds = galleryQuery.data?.map((gf) => gf.id) || [];

  // Available files are gallery folder files not in current gallery
  const availableFiles = galleryFiles.filter(
    (file) => !currentGalleryFileIds.includes(file.id),
  );

  const handleAddToGallery = async (fileId: string) => {
    if (currentGalleryFileIds.length >= 10) {
      return;
    }

    try {
      const newGalleryFileIds = [...currentGalleryFileIds, fileId];
      await updateGalleryMutation.mutateAsync({
        body: { fileIds: newGalleryFileIds },
      });
      galleryQuery.refetch();
    } catch (_error) {
      // Error handling could be added here if needed
    }
  };

  const handleRemoveFromGallery = async (fileId: string) => {
    try {
      const newGalleryFileIds = currentGalleryFileIds.filter(
        (id) => id !== fileId,
      );
      await updateGalleryMutation.mutateAsync({
        body: { fileIds: newGalleryFileIds },
      });
      galleryQuery.refetch();
    } catch (_error) {
      // Error handling could be added here if needed
    }
  };

  const getFileUrl = (file: FileOutput) => {
    if (file.isPublic) {
      return file.previewUrl;
    }
    return file.previewUrl;
  };

  const renderGalleryFileCard = (
    file: { id: string; name: string; type: string; url?: string | null },
    index: number,
  ) => {
    return (
      <Card key={file.id} withBorder radius="md" style={{ width: 120 }}>
        <Card.Section>
          <div
            style={{
              position: "relative",
              width: 120,
              height: 120,
              overflow: "hidden",
            }}
          >
            {file.type === "IMAGE" ? (
              <Image
                src={file.url}
                alt={file.name}
                height={120}
                width={120}
                fit="contain"
                fallbackSrc="/placeholder-image.png"
              />
            ) : (
              <div
                style={{
                  height: 120,
                  width: 120,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f8f9fa",
                }}
              >
                <IconVideo size={32} color="#868e96" />
              </div>
            )}
            <div
              style={{
                position: "absolute",
                top: 4,
                left: 4,
                backgroundColor: "#228be6",
                color: "white",
                borderRadius: "50%",
                width: 20,
                height: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {index + 1}
            </div>
            <Button
              size="xs"
              variant="filled"
              color="red"
              style={{ position: "absolute", top: 4, right: 4 }}
              onClick={() => handleRemoveFromGallery(file.id)}
              loading={updateGalleryMutation.isPending}
            >
              <IconArrowDown size={12} />
            </Button>
          </div>
        </Card.Section>
        <Text size="xs" mt="xs" truncate>
          {file.name}
        </Text>
      </Card>
    );
  };

  const renderAvailableFileCard = (file: FileOutput) => {
    return (
      <Card key={file.id} withBorder radius="md" style={{ width: 120 }}>
        <Card.Section>
          <div
            style={{
              position: "relative",
              width: 120,
              height: 120,
              overflow: "hidden",
            }}
          >
            {file.type === "IMAGE" ? (
              <Image
                src={getFileUrl(file)}
                alt={file.name}
                height={120}
                width={120}
                fit="contain"
                fallbackSrc="/placeholder-image.png"
              />
            ) : (
              <div
                style={{
                  height: 120,
                  width: 120,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f8f9fa",
                }}
              >
                <IconVideo size={32} color="#868e96" />
              </div>
            )}
            <Button
              size="xs"
              variant="filled"
              color="green"
              style={{ position: "absolute", top: 4, right: 4 }}
              onClick={() => handleAddToGallery(file.id)}
              loading={updateGalleryMutation.isPending}
              disabled={currentGalleryFileIds.length >= 10}
            >
              <IconArrowUp size={12} />
            </Button>
          </div>
        </Card.Section>
        <Text size="xs" mt="xs" truncate>
          {file.name}
        </Text>
      </Card>
    );
  };

  if (galleryQuery.isLoading || filesQuery.isLoading) {
    return <FancyLoader />;
  }

  return (
    <Stack>
      <Header title="Gallery" backLink={UniversalRoutes.PROFILE.HOME} />

      <Group justify="space-between" align="flex-start">
        <div>
          <Text size="lg" fw={500}>
            Manage Gallery
          </Text>
          <Text size="sm" c="dimmed">
            Add or remove up to 10 images/videos to feature in your public
            profile
          </Text>
        </div>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={openFileUpload}
          disabled={!galleryFolderId}
          variant="outline"
          size="sm"
        >
          Upload New File
        </Button>
      </Group>

      <Stack gap="lg">
        {/* Current Gallery Panel */}
        <Paper p="md" withBorder>
          <Group justify="space-between" mb="md">
            <Title order={4} c="blue">
              Current Gallery
            </Title>
            <Text size="sm" c="dimmed">
              {galleryQuery.data?.length || 0}/10
            </Text>
          </Group>

          {!galleryQuery.data || galleryQuery.data.length === 0 ? (
            <Paper p="lg" style={{ backgroundColor: "#f8f9fa" }}>
              <Stack align="center">
                <IconPhoto size={32} color="#868e96" />
                <Text ta="center" c="dimmed" size="sm">
                  No files in gallery yet
                </Text>
                <Text ta="center" c="dimmed" size="xs">
                  Add files from the available files panel
                </Text>
              </Stack>
            </Paper>
          ) : (
            <Flex wrap="wrap" gap="sm">
              {galleryQuery.data.map((file, index) =>
                renderGalleryFileCard(file, index),
              )}
            </Flex>
          )}
        </Paper>

        {/* Available Files Panel */}
        <Paper p="md" withBorder>
          <Group justify="space-between" mb="md">
            <Title order={4} c="green">
              Available Files
            </Title>
            <Text size="sm" c="dimmed">
              {availableFiles.length} files
            </Text>
          </Group>

          {availableFiles.length === 0 ? (
            <Paper p="lg" style={{ backgroundColor: "#f8f9fa" }}>
              <Stack align="center">
                <IconPhoto size={32} color="#868e96" />
                <Text ta="center" c="dimmed" size="sm">
                  {galleryFiles.length === 0
                    ? "No files in Gallery folder"
                    : "All files are already in gallery"}
                </Text>
                <Text ta="center" c="dimmed" size="xs">
                  {galleryFiles.length === 0 &&
                    "Upload files to your Gallery folder to get started"}
                </Text>
              </Stack>
            </Paper>
          ) : (
            <Flex wrap="wrap" gap="sm">
              {availableFiles.map(renderAvailableFileCard)}
            </Flex>
          )}
        </Paper>
      </Stack>

      {/* File upload modal */}
      {fileUploadModal}
    </Stack>
  );
};
